import NavMenu from '@/components/shared/NavMenu'
import StageStatus from '@/components/shared/stage-status'
import BackButton from '@/components/ui/BackButton'
import TabButton from '@/components/ui/TabButton'
import { isReadOnly } from '@/lib/permissions'
import { formatDateTime, scrollToSection } from '@/lib/utils'
import {
  INVESTIGATION_STATUS_LABELS,
  investigationStatusLabelToValue,
  investigationStatusValueToLabel
} from '@/constants/statuses'
import {
  type APIInvestigationDetail,
  useInvestigationDetailQuery,
  useUpdateInvestigationStatusMutation
} from '@/services/investigation-detail'
import { useUpdateMedReqDetailsMutation } from '@/services/med-req-detail'
import { useInvestigationStatusStages } from '@/hooks/useInvestigationStatusStages'
import type { Investigation } from '@/types/investigation'
import { Box, CircularProgress, Grid, Typography } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import NotesSection from '../med-requests/sections/notes-section'
import InvestigationDetails from './sections/details'
import InvestigationHeader from './sections/header'
import InvestigationTimeline from './sections/investigation-timeline'
import MedicalHistory from './sections/medical-history'
import TestResults from './sections/test-results'

const navItems = [
  { id: 'investigation-details', title: 'Patient details' },
  { id: 'medical-history', title: 'Medical history' },
  { id: 'test-results', title: 'Test results' },
  { id: 'additional-notes', title: 'Additional notes' }
]

function toInvestigation(data: APIInvestigationDetail): Investigation {
  return {
    id: data.orderItemHash,
    name: data.orderItemName ?? data.productName ?? '',
    externalId: data.externalId ?? '',
    status: data.orderItemStatus ?? '',
    date: data.createdAt ?? '',
    patientId: data.patientHash ?? '',
    patientName: data.patientName ?? '',
    orderId: data.orderHash ?? undefined,
    orderNumber: data.orderNumber ?? undefined,
    product: data.productName ?? undefined,
    productHash: data.productHash ?? undefined,
    productVariationHash: data.productVariationHash ?? undefined,
    result: undefined,
    resultRaw: data.resultsRaw ?? undefined,
    medications: data.medications ?? undefined,
    allergies: data.allergies ?? undefined,
    familyHistory: data.familyHistory ?? undefined,
    conditions: data.conditions ?? undefined,
    pharmacyNotes: data.notes ?? undefined,
    customerServiceNotes: data.csNotes ?? undefined,
    updatedAt: data.updatedAt ? new Date(data.updatedAt) : undefined
  }
}

export default function InvestigationDetailPage() {
  const { id } = useParams<{ id: string }>()
  const queryClient = useQueryClient()
  const readOnly = isReadOnly()
  const [showTimeline, setShowTimeline] = useState(false)
  const updateStatusMutation = useUpdateInvestigationStatusMutation()
  const updateDetailsMutation = useUpdateMedReqDetailsMutation()
  const { data: apiData, isLoading, isError } = useInvestigationDetailQuery(id)
  const { stages, isLoading: isStagesLoading } = useInvestigationStatusStages(id ?? '', apiData?.createdAt ?? undefined)

  const investigation = apiData ? toInvestigation(apiData) : undefined

  const handleStatusChange = async (stage: { label: string }) => {
    if (isReadOnly()) return
    if (!id) return
    const backendValue = investigationStatusLabelToValue(stage.label)
    if (!backendValue) return

    await updateStatusMutation.mutateAsync({ orderItemHash: id, status: backendValue })
    queryClient.invalidateQueries({ queryKey: ['investigation-detail', id] })
    queryClient.invalidateQueries({ queryKey: ['timeline', 'investigation', id] })
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress size={32} />
      </Box>
    )
  }

  if (isError || !investigation) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography>Investigation not found</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, pb: 12 }}>
      {/* Page Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
        <BackButton fallbackPath={`/patients/${investigation.patientId}`} defaultLabel='Back to Patient' />
        <Typography variant='pageTitle' sx={{ fontSize: 24 }}>
          Investigation
        </Typography>
        <TabButton active={showTimeline} onClick={() => setShowTimeline(prev => !prev)}>
          Timeline
        </TabButton>
      </Box>

      <InvestigationHeader investigation={investigation} />

      {showTimeline ? (
        <InvestigationTimeline
          investigationHash={String(investigation.id)}
          investigationName={`${investigation.name}${investigation.externalId ? ` - ${investigation.externalId}` : ''}`}
        />
      ) : (
        <>
          <StageStatus
            key={isStagesLoading ? 'loading' : 'timeline'}
            stages={stages}
            config={{
              title: 'Investigation status',
              statusOptions: INVESTIGATION_STATUS_LABELS,
              fallbackStatus: investigationStatusValueToLabel(investigation.status),
              changedBy: undefined,
              updatedAt: formatDateTime(stages[stages.length - 1]?.date ?? new Date()),
              previousStatus: stages.length >= 2 ? stages[stages.length - 2]?.label : undefined
            }}
            onStageAdd={readOnly ? undefined : handleStatusChange}
            readOnly={readOnly}
          />

          <Grid container spacing={4}>
            <Grid size={9} sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <InvestigationDetails investigation={investigation} />
              <MedicalHistory investigation={investigation} />
              <TestResults investigation={investigation} />
              <Box id='additional-notes' sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <NotesSection
                  title='Pharmacy notes'
                  notes={investigation.pharmacyNotes}
                  readOnly={readOnly}
                  onSave={async notes => {
                    await updateDetailsMutation.mutateAsync({
                      orderItemHash: String(investigation.id),
                      data: { notes }
                    })
                    queryClient.invalidateQueries({ queryKey: ['investigation-detail', id] })
                  }}
                />
                <NotesSection
                  title='Customer Service Notes'
                  notes={investigation.customerServiceNotes}
                  readOnly={readOnly}
                  onSave={async notes => {
                    await updateDetailsMutation.mutateAsync({
                      orderItemHash: String(investigation.id),
                      data: { cs_notes: notes }
                    })
                    queryClient.invalidateQueries({ queryKey: ['investigation-detail', id] })
                  }}
                />
              </Box>
            </Grid>

            <Grid size={3} sx={{ position: 'sticky', top: 88, alignSelf: 'flex-start' }}>
              <NavMenu items={navItems} onNavigate={scrollToSection} />
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  )
}
